/**
 * Layout.jsx - REIMAGINED
 * Modern header with alerts dropdown and blinking badge
 */

import React, { useState, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { NavLink } from 'react-router-dom';
import useStore from '../store/useStore';
import { format } from 'date-fns';
import './Layout.css';
import logo from '../assets/lekoil-logo.svg';

const Layout = ({ children }) => {
  const { instance } = useMsal();
  const [showAlertsDropdown, setShowAlertsDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const alertsRef = useRef(null);
  const userMenuRef = useRef(null);
  
  const { 
    user, 
    assets, 
    selectedAsset, 
    selectAsset, 
    userRole,
    unresolvedAlerts,
    alerts,
    resolveAlert
  } = useStore();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (alertsRef.current && !alertsRef.current.contains(event.target)) {
        setShowAlertsDropdown(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    instance.logoutPopup();
  };

const handleAssetChange = async (e) => {
  const assetId = parseInt(e.target.value);
   console.log('Selected asset ID:', assetId);
  if (assetId) {
    await selectAsset(assetId);
  }
};

  const handleResolveAlert = async (alertId, e) => {
    e.stopPropagation();
    try {
      await resolveAlert(alertId);
    } catch (error) {
      console.error('Failed to resolve alert:', error);
    }
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'Critical') return '🔴';
    if (severity === 'Warning') return '⚠️';
    return 'ℹ️';
  };

  const getSeverityClass = (severity) => {
    if (severity === 'Critical') return 'critical';
    if (severity === 'Warning') return 'warning';
    return 'info';
  };

  return (
    <div className="layout">
      <header className="header">
        <div className="header-left">
          {/* Logo */}
          <div className="logo-section">
  <div className="logo-icon">
   <img src={logo} alt="Logo" width="50" height="40" />
  </div>
  <div className="logo-text">
    <h1>Asset Performance</h1>
    <span className="tagline">Real-time Analytics Dashboard</span>
  </div>
</div>

          {/* Page Navigation */}
          <nav className="header-nav">
            <NavLink
              to="/"
              end
              className={({ isActive }) => `header-nav__link ${isActive ? 'header-nav__link--active' : ''}`}
            >
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                <path d="M3 9.5L10 3L17 9.5V17H13V13H7V17H3V9.5Z" fill="currentColor"/>
              </svg>
              Overview
            </NavLink>
            <NavLink
              to="/dashboard"
              className={({ isActive }) => `header-nav__link ${isActive ? 'header-nav__link--active' : ''}`}
            >
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                <rect x="2" y="2" width="7" height="7" rx="1.5" fill="currentColor"/>
                <rect x="11" y="2" width="7" height="7" rx="1.5" fill="currentColor"/>
                <rect x="2" y="11" width="7" height="7" rx="1.5" fill="currentColor"/>
                <rect x="11" y="11" width="7" height="7" rx="1.5" fill="currentColor"/>
              </svg>
              Dashboard
            </NavLink>
            <NavLink
              to="/rules"
              className={({ isActive }) => `header-nav__link ${isActive ? 'header-nav__link--active' : ''}`}
            >
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                <path d="M4 5h12M4 10h8M4 15h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <circle cx="16" cy="10" r="2.5" fill="currentColor"/>
              </svg>
              Rules
            </NavLink>
            <NavLink
              to="/notifications"
              className={({ isActive }) => `header-nav__link ${isActive ? 'header-nav__link--active' : ''}`}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C10.9 2 10 2.9 10 4V4.29C7.03 5.17 5 7.9 5 11V17L3 19V20H21V19L19 17V11C19 7.9 16.97 5.17 14 4.29V4C14 2.9 13.1 2 12 2ZM12 23C13.1 23 14 22.1 14 21H10C10 22.1 10.9 23 12 23Z" fill="currentColor"/>
              </svg>
              Notifications
            </NavLink>
          </nav>

          {/* Asset Selector */}
          {assets.length > 0 && (
            <div className="asset-selector">
              <label htmlFor="asset-select">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2L14 5V11L8 14L2 11V5L8 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
                Asset
              </label>
              <select 
  id="asset-select"
  value={selectedAsset?.Id || ''} 
  onChange={handleAssetChange}
  disabled={assets.length === 0}
>
  {assets.map(asset => (
    <option key={asset.Id} value={asset.Id}>
      {asset.Title} {asset.AssetCode ? `(${asset.AssetCode})` : ''}
    </option>
  ))}
</select>
            </div>
          )}
        </div>

        <div className="header-right">
          {/* Alerts Dropdown */}
          <div className="alerts-container" ref={alertsRef}>
            <button 
              className="alerts-button"
              onClick={() => setShowAlertsDropdown(!showAlertsDropdown)}
              title="Alerts"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C10.9 2 10 2.9 10 4V4.29C7.03 5.17 5 7.9 5 11V17L3 19V20H21V19L19 17V11C19 7.9 16.97 5.17 14 4.29V4C14 2.9 13.1 2 12 2ZM12 23C13.1 23 14 22.1 14 21H10C10 22.1 10.9 23 12 23Z" 
                      fill="currentColor"/>
              </svg>
              {unresolvedAlerts?.length > 0 && (
                <span className="alerts-badge blink">{unresolvedAlerts.length}</span>
              )}
            </button>

            {showAlertsDropdown && (
              <div className="alerts-dropdown">
                <div className="dropdown-header">
                  <h3>
                    🔔 Alerts
                    {unresolvedAlerts?.length > 0 && (
                      <span className="badge-count">{unresolvedAlerts.length} unresolved</span>
                    )}
                  </h3>
                </div>
                
                <div className="alerts-list">
                  {unresolvedAlerts?.length === 0 ? (
                    <div className="no-alerts">
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="20" fill="#DFF6DD"/>
                        <path d="M16 24L21 29L32 18" stroke="#107C10" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <p>All clear! No active alerts.</p>
                    </div>
                  ) : (
                    unresolvedAlerts?.slice(0, 5).map(alert => (
                      <div key={alert.Id} className={`alert-item ${getSeverityClass(alert.Severity)}`}>
                        <div className="alert-header">
                          <span className="severity-icon">{getSeverityIcon(alert.Severity)}</span>
                          <span className={`severity-label ${getSeverityClass(alert.Severity)}`}>
                            {alert.Severity}
                          </span>
                          <span className="alert-time">
                            {format(new Date(alert.Created), 'MMM dd, HH:mm')}
                          </span>
                        </div>
                        <div className="alert-message">{alert.Message}</div>
                        {alert.Explanation && (
                          <div className="alert-explanation">{alert.Explanation}</div>
                        )}
                        <button 
                          className="resolve-btn"
                          onClick={(e) => handleResolveAlert(alert.Id, e)}
                        >
                          ✓ Resolve
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {unresolvedAlerts?.length > 5 && (
                  <div className="dropdown-footer">
                    <span>Showing 5 of {unresolvedAlerts.length} alerts</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="user-container" ref={userMenuRef}>
            <button 
              className="user-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.name || 'User'}</span>
                <span className="user-role">{userRole}</span>
              </div>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="chevron">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-menu-header">
                  <div className="user-avatar-lg">
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div>
                    <div className="user-name-lg">{user?.name || 'User'}</div>
                    <div className="user-email">{user?.username || user?.email}</div>
                  </div>
                </div>
                <div className="user-menu-divider"></div>
                <div className="user-menu-role">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z" fill="currentColor"/>
                    <path d="M3 14C3 11.7909 4.79086 10 7 10H9C11.2091 10 13 11.7909 13 14H3Z" fill="currentColor"/>
                  </svg>
                  <span>Role: <strong>{userRole}</strong></span>
                </div>
                <div className="user-menu-divider"></div>
                <button className="logout-menu-btn" onClick={handleLogout}>
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path d="M9 3H5C3.89543 3 3 3.89543 3 5V15C3 16.1046 3.89543 17 5 17H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M13 7L17 10L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17 10H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;